"use client";

import { c as _c } from "react/compiler-runtime";
import type { QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { authClient } from "@/lib/auth-client";
import { bindReactQueryOnlineManager, clearFailedOfflineScoreMutations, getOnlineStatusSnapshot, hydrateOfflineScoreQueue, setOfflineScoreQueueUserScope, syncQueuedScoreMutations, useOfflineScoreQueueState, useOnlineStatus } from "@/lib/shared/offline";
import { cn } from "@/lib/utils";
function OfflineStatusBanner() {
  const $ = _c(13);
  const isOnline = useOnlineStatus();
  const queueState = useOfflineScoreQueueState();
  let message = null;
  if (!isOnline) {
    message = queueState.pendingCount > 0 ? `Offline. ${queueState.pendingCount} score entr${queueState.pendingCount === 1 ? "y is" : "ies are"} queued and onboarding progress is saved on this device.` : "Offline. New score entries will queue automatically and onboarding progress is saved on this device.";
  } else {
    if (queueState.isSyncing) {
      message = `Back online. Syncing ${queueState.pendingCount} queued score entr${queueState.pendingCount === 1 ? "y" : "ies"}...`;
    } else {
      if (queueState.failedCount > 0) {
        message = `${queueState.failedCount} queued score entr${queueState.failedCount === 1 ? "y needs" : "ies need"} attention before they can sync.`;
      }
    }
  }
  if (!message) {
    return null;
  }
  const t0 = !isOnline && "bg-amber-500/95 text-amber-950";
  const t1 = isOnline && queueState.isSyncing && "bg-foreground text-background";
  const t2 = isOnline && !queueState.isSyncing && queueState.failedCount > 0 && "bg-destructive text-destructive-foreground";
  let t3;
  if ($[0] !== t0 || $[1] !== t1 || $[2] !== t2) {
    t3 = cn("pointer-events-auto flex max-w-2xl items-center gap-3 rounded-full px-4 py-2 font-medium text-sm shadow-lg ring-1 ring-foreground/10 backdrop-blur", t0, t1, t2);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== message) {
    t4 = <span>{message}</span>;
    $[4] = message;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isOnline || $[7] !== queueState.failedCount) {
    t5 = isOnline && queueState.failedCount > 0 ? <button type="button" onClick={_temp} className="rounded-full bg-background/15 px-2.5 py-1 font-semibold text-xs transition-colors hover:bg-background/25">Dismiss failed entries</button> : null;
    $[6] = isOnline;
    $[7] = queueState.failedCount;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = <div className="pointer-events-none fixed inset-x-0 top-4 z-50 flex justify-center px-4"><output aria-live="polite" className={t3}>{t4}{t5}</output></div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
function _temp() {
  clearFailedOfflineScoreMutations();
}
export function OfflineSyncProvider(t0) {
  const $ = _c(14);
  const {
    children,
    queryClient
  } = t0;
  const isOnline = useOnlineStatus();
  const {
    data: session
  } = authClient.useSession();
  let t1;
  if ($[0] !== session?.user?.id) {
    t1 = () => {
      setOfflineScoreQueueUserScope(session?.user?.id ?? null);
    };
    $[0] = session?.user?.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = session?.user?.id;
  let t3;
  if ($[2] !== t2) {
    t3 = [t2];
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(t1, t3);
  let t4;
  let t5;
  if ($[4] !== queryClient) {
    t4 = () => {
      bindReactQueryOnlineManager();
      hydrateOfflineScoreQueue().then(() => {
        if (getOnlineStatusSnapshot()) {
          syncQueuedScoreMutations(queryClient);
        }
      });
    };
    t5 = [queryClient];
    $[4] = queryClient;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[7] !== isOnline || $[8] !== queryClient) {
    t6 = () => {
      if (!isOnline) {
        return;
      }
      syncQueuedScoreMutations(queryClient);
    };
    t7 = [isOnline, queryClient];
    $[7] = isOnline;
    $[8] = queryClient;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <OfflineStatusBanner />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== children) {
    t9 = <>{children}{t8}</>;
    $[12] = children;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}